import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithColumns,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import { type BaseSchema, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { FormGroupChildrenHandlerService } from '../form-group/form-group-children-handler.service';
import { FormGroupOutputEmitGuard } from '../form-group/form-group-output-emit.guard';

import { wizardV2Migration } from './migrations/002-wizard.migration';
import { WizardValidationHandler } from './wizard-validation-handler.service';
import { WizardComponent } from './wizard.component';
import { getInitialFormSteps } from './wizard.constants';
import { type WizardPropsModel } from './wizard.models';
import { WizardPropsBuilder } from './wizard.props-builder';
import { getPropsConfig } from './wizard.props-config';
import { WizardPropsExtractor } from './wizard.props-extractor';
import { getWizardTextStyleGroup, resolveWizardTextStyleOverrides } from './wizard.utils';

export type WizardDef = WithColumns & WithProps<WizardPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    wizard: WizardDef;
  }
}

const INITIAL_SLOT_ID = '0';
const CONFIRMATION_SLOT_ID = '1';
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 420;

const WIZARD_DEFAULTS = getDefaults();

registerElementDefinition({
  type: 'wizard',
  componentType: WizardComponent,
  outputEmitGuard: FormGroupOutputEmitGuard,
  childrenHandler: FormGroupChildrenHandlerService,
  validationHandler: WizardValidationHandler,
  isListed: true,
  name: 'Wizard',
  width: { mode: 'value', value: DEFAULT_WIDTH },
  // By default the element should have a min-height and grow automatically if needed
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  slots: {
    ids: [INITIAL_SLOT_ID, CONFIRMATION_SLOT_ID],
    [INITIAL_SLOT_ID]: {
      children: [],
    },
    [CONFIRMATION_SLOT_ID]: {
      children: [],
    },
  },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: WizardPropsBuilder,
  propsExtractor: WizardPropsExtractor,
  defaults: WIZARD_DEFAULTS,
  initial: getInitial(),
  // Questionnaire text elements pull their typography from the wizard's
  // style.question/style.explanation (resolved against the definition defaults - raw props
  // may not carry them yet). Which of the two a child gets is answered from the step that
  // records it, so only elements this wizard still generates are styled by it.
  childrenOverridesResolver: (props, childId) => {
    const group = getWizardTextStyleGroup(props, childId);

    return group
      ? resolveWizardTextStyleOverrides(WIZARD_DEFAULTS.style[group], props.style?.[group])
      : undefined;
  },
  groups: ['forms'],
  styleIncludePaths: [
    'style',
    'content.layout',
    'content.title.align',
    'content.title.iconPosition',
  ],
  features: {
    preventAutoWidth: true,
    visualStyle: 'block',
  },
  version: 2,
  migrations: {
    2: wizardV2Migration,
  },
});

function getIoConfig(): IoConfig {
  const SLOT_ID_SCHEMA: BaseSchema = {
    id: '',
    name: 'slot id',
    columns: [
      {
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
        name: '',
      },
    ],
  };
  const SLOT_ID_STEP_IDX_SCHEMA: BaseSchema = {
    id: '',
    name: 'slot id step idx',
    columns: [
      {
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
        name: 'slotId',
      },
      {
        columnType: 'Number',
        dataType: 'Number',
        isProfilePicture: false,
        name: 'currentStepIdx',
      },
    ],
  };

  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'change', schema: DEFAULT_SCHEMA },
      { eventType: 'next', schema: SLOT_ID_STEP_IDX_SCHEMA },
      { eventType: 'status', schema: DEFAULT_SCHEMA }, // schema set in wizard.component
      { eventType: 'submit', schema: SLOT_ID_SCHEMA },
    ],
  };
}

function getDefaults(): PropsOf<'wizard'> {
  return {
    content: {
      mode: 'form',
      model: [],
      saveDraftEnabled: true,
      layout: {
        style: 'numbers',
        numerateTabs: true,
        progressPercent: { enabled: true, position: 'right' },
        placement: 'top',
        align: { horizontal: 'justify' },
      },
      title: {
        enabled: true,
        label: 'Wizard',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
      },
      title: {
        text: { color1: '$t.title', selectorType: 'solid' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      question: {
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          fontSize: '20px',
          lineHeight: '20px',
          letterSpacing: '0px',
          fontWeight: '500' as FontWeightOption,
        },
        textColor: { color1: '$t.title', selectorType: 'solid' },
      },
      explanation: {
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          fontSize: '$t.fontSize',
          lineHeight: '$t.lineHeight',
          letterSpacing: '0px',
          fontWeight: '$t.fontWeight' as FontWeightOption,
        },
        textColor: { color1: '$t.value', selectorType: 'solid' },
      },
      buttons: {
        buttonsView: 'primary',
        primary: {
          text: { color1: '$t.accentLabel', selectorType: 'solid' },
          fill: { color1: '$t.accent', selectorType: 'solid' },
          outline: { color1: '$t.accent', selectorType: 'solid' },
        },
        secondary: {
          text: { color1: '$t.value', selectorType: 'solid' },
          fill: { color1: '$t.fill', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'solid' },
        },
      },
      container: {
        fill: { color1: '$t.canvas', selectorType: 'none' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'wizard'> {
  return {
    content: {
      action: 'add',
      steps: {
        items: getInitialFormSteps(),
      },
    },
    style: {},
  };
}
